// Shared sync-run presentation + finish-estimation helpers, used by the activity
// area (and the legacy SyncLogList). ETA is computed from the run's structured
// progress ({done,total}) + how long it has been running — accurate where a
// denominator exists, indeterminate otherwise.

/**
 * Minimal structural shape the progress helpers need. BOTH `SyncRun` and the
 * Activity Center's `Activity` satisfy this (same `progress`/`status`/`startedAt`
 * fields), so `progressFraction`/`etaLabel` work for either WITHOUT a cast.
 * `status` is widened to `string` so any status vocabulary passes — only the
 * literal `'running'` is treated specially.
 */
export interface ProgressLike {
  progress?: { phase?: string; done: number; total: number } | null;
  status: string;
  startedAt: string;
}

export const KIND_ICON: Record<string, string> = {
  discourse: '💬', commits: '⎇', 'azure-devops': '🔷', release: '🚀', knowledge: '📚',
  zoho: '🎫', documents: '📄', 'zoho-classify': '🏷', 'zoho-topics': '🧭', 'zoho-embed': '🧬',
  entitlements: '🎟', repos: '📦', 'coco-embed': '📦', 'coco-links': '🔗', realequity: '🏙️',
  'zoho-attachments': '📎', 'azure-attachments': '📎', 'discourse-attachments': '📎',
  'zoho-runall': '▶', 'azure-runall': '▶', 'discourse-runall': '▶',
};

export const STATUS_STYLE: Record<string, { label: string; color: string; bg: string }> = {
  running: { label: 'running', color: 'var(--accent-primary)', bg: 'var(--accent-primary-soft)' },
  completed: { label: 'done', color: 'var(--accent-success)', bg: 'rgba(102,187,106,0.14)' },
  partial: { label: 'partial', color: 'var(--accent-warning)', bg: 'rgba(217,119,6,0.14)' },
  failed: { label: 'failed', color: 'var(--accent-error)', bg: 'rgba(239,83,80,0.14)' },
  stopped: { label: 'stopped', color: 'var(--accent-warning)', bg: 'rgba(217,119,6,0.14)' },
  interrupted: { label: 'interrupted', color: 'var(--text-tertiary)', bg: 'var(--bg-elevated)' },
  rejected: { label: 'rejected', color: 'var(--accent-warning)', bg: 'rgba(217,119,6,0.14)' },
};

/** Which integration group a run kind belongs to (for grouping the activity list). */
export function kindGroup(kind: string): string {
  if (kind.startsWith('zoho')) return 'Zoho';
  if (kind === 'azure-devops' || kind.startsWith('azure')) return 'Azure DevOps';
  if (kind.startsWith('discourse')) return 'Discourse';
  if (kind === 'commits' || kind === 'release') return 'GitHub';
  if (kind === 'repos' || kind.startsWith('coco')) return 'CoCo';
  if (kind === 'knowledge' || kind === 'documents') return 'Knowledge';
  if (kind === 'realequity') return 'RealEquity';
  return 'Other';
}

export function duration(start: string, end: string | null): string {
  const a = new Date(start).getTime();
  const b = end ? new Date(end).getTime() : Date.now();
  const s = Math.max(0, Math.round((b - a) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
}

function humanSecs(s: number): string {
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `~${Math.round(s / 60)}m`;
  return `~${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;
}

/** Determinate fraction [0..1] for a running job, or null if no denominator. */
export function progressFraction(run: ProgressLike): number | null {
  const p = run.progress;
  if (!p || !p.total || p.total <= 0) return null;
  return Math.max(0, Math.min(1, p.done / p.total));
}

/** Best-effort finish estimate: "~Xm remaining" from done/total + elapsed, or
 *  null when there's no denominator or not enough signal yet. */
export function etaLabel(run: ProgressLike): string | null {
  const p = run.progress;
  if (run.status !== 'running' || !p || !p.total || p.total <= 0 || p.done <= 0 || p.done >= p.total) return null;
  const elapsedSec = Math.max(1, (Date.now() - new Date(run.startedAt).getTime()) / 1000);
  const rate = p.done / elapsedSec; // items/sec
  if (!Number.isFinite(rate) || rate <= 0) return null;
  return `${humanSecs((p.total - p.done) / rate)} remaining`;
}
